import {Dispatch} from "@reduxjs/toolkit";
import {
    finishAssistantMessage,
    updateThread,
    replaceMessage,
    addSourceToLastMessage,
    updateThreadName,
    updateThreadId,
    rateLimitReset,
    addAgentThought,
    addAgentActivity,
    addPluginActivity,
    addRelatedToolsToLastMessage,
} from "./chatSlice";
import {
    addLowScoreMessage,
    addNoAnswerMessage,
    addNoSourcesMessage,
    addStreamError,
    initiateTransientStore,
} from "./transientChatSlice";
import {chatApi} from "./chatApi";
import {ChatStopReason, MessageEnum, StreamErrorType} from "../../types";

export interface MessageHandlerContext {
    dispatch: Dispatch;
    accumulatedText: string;
    delta: Date;
    onThreadIdReceived?: (threadId: string) => void;
}

export type MessageHandlerResult = {
    accumulatedText?: string;
    delta?: Date;
    accumulatedDispatched?: boolean;
};

export type MessageHandler = (
    parsed: any,
    context: MessageHandlerContext
) => MessageHandlerResult;

const handleThreadCreated: MessageHandler = (
    parsed,
    {dispatch, onThreadIdReceived}
) => {
    dispatch(updateThreadId(parsed.threadId));
    dispatch(initiateTransientStore(parsed.threadId));
    onThreadIdReceived?.(parsed.threadId);
    return {};
};

const handleThreadMetaMessage: MessageHandler = (
    parsed,
    {dispatch, onThreadIdReceived}
) => {
    dispatch(
        updateThread({
            id: parsed.threadId,
            name: parsed.name,
            messages: parsed.messages,
            datestamp: parsed.datestamp,
        })
    );
    onThreadIdReceived?.(parsed.threadId);
    return {};
};

const handleThreadNameMessage: MessageHandler = (parsed, {dispatch}) => {
    dispatch(
        updateThreadName({
            threadId: parsed.threadId,
            threadName: parsed.threadName,
        })
    );
    return {};
};

const handleMessageWithContent: MessageHandler = (
    parsed,
    {dispatch, accumulatedText, delta}
) => {
    const content = parsed.content;
    if (!content) {
        return {};
    }

    const newAccumulatedText = accumulatedText + content;
    const timeSinceDelta = new Date().getTime() - delta.getTime();

    // Limit the amount of updates to the redux store
    if (timeSinceDelta > 100) {
        dispatch(replaceMessage(newAccumulatedText));
        return {
            accumulatedText: newAccumulatedText,
            delta: new Date(),
        };
    }

    return {accumulatedText: newAccumulatedText};
};

const handleStopMessage: MessageHandler = (
    parsed,
    {dispatch, accumulatedText}
) => {
    dispatch(
        finishAssistantMessage({
            text: accumulatedText,
            stopReason: parsed.stop_reason as ChatStopReason,
        })
    );
    return {accumulatedDispatched: true};
};

const handleErrorMessage: MessageHandler = (parsed, {dispatch}) => {
    dispatch(addStreamError(parsed));
    if (parsed.errorType === StreamErrorType.RateLimitExceeded) {
        dispatch(rateLimitReset());
        return {accumulatedText: ""};
    }
    return {};
};

const handleRagSourceMessage: MessageHandler = (parsed, {dispatch}) => {
    dispatch(addSourceToLastMessage(parsed));
    return {};
};

const handleRelatedToolsMessage: MessageHandler = (parsed, {dispatch}) => {
    dispatch(addRelatedToolsToLastMessage(parsed));
    return {};
};

const handleNoOp: MessageHandler = () => ({});

const handleAgentActivity: MessageHandler = (parsed, {dispatch}) => {
    dispatch(addAgentActivity(parsed));
    return {};
};

const handleAgentThought: MessageHandler = (parsed, {dispatch}) => {
    dispatch(addAgentThought(parsed));
    return {};
};

const handlePluginActivity: MessageHandler = (parsed, {dispatch}) => {
    dispatch(addPluginActivity(parsed));
    return {};
};

const handleLowScoreMessage: MessageHandler = (parsed, {dispatch}) => {
    dispatch(addLowScoreMessage(parsed));
    return {};
};

const handleNoAnswerMessage: MessageHandler = (parsed, {dispatch}) => {
    dispatch(addNoAnswerMessage(parsed));
    return {};
};

const handleNoSourcesMessage: MessageHandler = (parsed, {dispatch}) => {
    dispatch(addNoSourcesMessage(parsed));
    return {};
};

// No-op handlers for messages that are currently commented out

export const MESSAGE_HANDLERS: Record<MessageEnum, MessageHandler> = {
    [MessageEnum.ThreadCreated]: handleThreadCreated,
    [MessageEnum.ThreadMetaMessage]: handleThreadMetaMessage,
    [MessageEnum.ThreadNameMessage]: handleThreadNameMessage,
    [MessageEnum.ChatCompletionMessage]: handleMessageWithContent,
    [MessageEnum.MessageSegment]: handleMessageWithContent,
    [MessageEnum.StopMessage]: handleStopMessage,
    [MessageEnum.RagProcessMessage]: handleNoOp,
    [MessageEnum.FilteredContentMessage]: handleNoOp,
    [MessageEnum.ThinkingCompleteMessage]: handleNoOp,
    [MessageEnum.FollowUpMessage]: handleNoOp,
    [MessageEnum.UsageMessage]: handleNoOp,
    [MessageEnum.ErrorMessage]: handleErrorMessage,
    [MessageEnum.RagSourceMessage]: handleRagSourceMessage,
    [MessageEnum.RelatedToolsMessage]: handleRelatedToolsMessage,
    [MessageEnum.AgentActivity]: handleAgentActivity,
    [MessageEnum.AgentThought]: handleAgentThought,
    [MessageEnum.PluginActivity]: handlePluginActivity,
    [MessageEnum.LowScoreMessage]: handleLowScoreMessage,
    [MessageEnum.NoAnswerMessage]: handleNoAnswerMessage,
    [MessageEnum.NoScoreMessage]: handleNoSourcesMessage,
};

export const handleMessage = (
    messageType: MessageEnum | null,
    parsed: any,
    context: MessageHandlerContext
): MessageHandlerResult => {
    if (messageType === null) {
        console.warn("Unknown message type received:", parsed);
        return {};
    }

    const handler = MESSAGE_HANDLERS[messageType];
    if (!handler) {
        console.warn(
            `No handler found for message type: ${MessageEnum[messageType]}`
        );
        return {};
    }

    try {
        return handler(parsed, context);
    } catch (error) {
        console.error(
            `Error handling message type ${MessageEnum[messageType]}:`,
            error,
            parsed
        );
        return {};
    }
};
